import { CommonModule } from '@angular/common';
import { Component, input } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ErrorTooltipDirective } from '../directives/error-tooltip';
import { fieldHasVisibleErrors } from '../utils/field-tree';

import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
/**
 * Row layout that wraps a form control and shows an **"Invalid"** hint
 * with a tooltip icon when the field is invalid and dirty or touched.
 *
 * The tooltip reads validation messages via `ErrorTooltipFromDirective`,
 * which delegates to `FormErrorService.messagesFromValidationErrors`
 * from `@aurrica-web/core-data`.
 *
 * Exposes itself as `formError` via `exportAs` so parent templates can
 * read `hasError` to drive CSS classes on the sibling input.
 *
 * @example
 * ```html
 * <lib-ui-form-error [form]="loginForm" ctrlName="email" #emailError="formError">
 *   <lib-ui-form-label label="Email" />
 * </lib-ui-form-error>
 * <input
 *   type="email"
 *   [formField]="loginForm.email"
 *   class="form-control"
 *   [class.form-control-error]="emailError.hasError"
 * />
 * ```
 */
@Component({
  selector: 'lib-ui-form-error',
  imports: [CommonModule, FontAwesomeModule, ErrorTooltipDirective],
  exportAs: 'formError',
  template: `
    <div class="flex flex-row justify-between items-center w-full">
      <div class="block w-full">
        <ng-content></ng-content>
      </div>
      @if (hasErrors(ctrlName())) {
        <div class="flex flex-row gap-1 text-xs">
          <span class="text-red-500">Invalid</span>
          <fa-icon
            [icon]="circleInfoIcon"
            [libUiErrorTooltip]="{
              root: form(),
              control: ctrlName(),
            }"
            [placement]="tooltipPlacement()"
            class="text-red-500 cursor-pointer"
          />
        </div>
      }
    </div>
  `,
})
export class FormErrorWrapper {
  protected readonly circleInfoIcon = faCircleInfo;
  /** Root `FieldTree` returned by `form(model, …)` from `@angular/forms/signals`. */
  form = input.required<FieldTree<unknown>>();

  /** Top-level model key matching a property on the form model (e.g. `'email'`, `'password'`). */
  ctrlName = input.required<string>();

  /**
   * Position of the validation tooltip relative to the info icon.
   * @defaultValue `'right'`
   */
  tooltipPlacement = input<'right' | 'left' | 'top' | 'bottom'>('right');

  /** @internal */
  hasErrors(controlName: string): boolean {
    return fieldHasVisibleErrors(this.form(), controlName);
  }

  /** `true` when the bound control is invalid and dirty or touched. */
  get hasError(): boolean {
    return this.hasErrors(this.ctrlName());
  }
}
